/**
 * IndexedDB cache for static entity data (cameras, sensors, geofences, etc.)
 *
 * On load: read from cache → show instantly → fetch API in background → update if changed.
 * Reduces initial load from ~6s to ~100ms for cached resources.
 *
 * DB: 'kalan-map-v2-cache'
 * Store: 'entities' — key: `${userId}|${entityType}`, value: { scope, data, timestamp, count }
 *
 * Aislada por usuario. Antes la clave era solo el entityType y el operador que
 * entraba en el mismo navegador recibía, durante 10 min, las entidades y
 * geocercas del que acababa de salir — con devices_by_geofence, las geocercas
 * ajenas se sumaban al filtro y le mostraban la zona del otro. Medido en prod
 * (2026-10-13): Ixtapaluca después de Texcoco cargó 1,733 objetos y 9
 * geocercas de Texcoco. Dos candados: la clave lleva el usuario y la entrada
 * guarda su dueño (`scope`), que se vuelve a comprobar al leer. Sin usuario no
 * hay caché. clearCache() en login/logout (auth.js) borra además los datos en
 * reposo: el logout dejaba en disco un volcado completo del último usuario.
 */

var DB_NAME = 'kalan-map-v2-cache'
var DB_VERSION = 1
var STORE_NAME = 'entities'
export var MAX_AGE_MS = 10 * 60 * 1000 // 10 minutes TTL

export function scopedKey(scope, key) {
  if (scope == null || scope === '') return null
  return scope + '|' + key
}

export function isUsableEntry(entry, scope, now) {
  if (!entry || scope == null || entry.scope == null) return false
  if (String(entry.scope) !== String(scope)) return false
  return now - entry.timestamp <= MAX_AGE_MS
}

var dbPromise = null

function openDB() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise(function(resolve, reject) {
    var request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = function(event) {
      var db = event.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }

    request.onsuccess = function(event) {
      resolve(event.target.result)
    }

    request.onerror = function() {
      console.warn('[entity-cache] IndexedDB failed to open')
      reject(request.error)
    }
  })

  return dbPromise
}

/**
 * Get cached data for an entity type of a given user.
 * Returns null if not cached, expired, owned by another user or no user.
 *
 * @param {string} key - entity type or cache key
 * @param {number|string} scope - id del usuario de la sesión
 * @returns {Promise<Array|null>}
 */
export function getCached(key, scope) {
  var fullKey = scopedKey(scope, key)
  if (!fullKey) return Promise.resolve(null)
  return openDB().then(function(db) {
    return new Promise(function(resolve) {
      var tx = db.transaction(STORE_NAME, 'readonly')
      var store = tx.objectStore(STORE_NAME)
      var request = store.get(fullKey)

      request.onsuccess = function() {
        var entry = request.result
        resolve(isUsableEntry(entry, scope, Date.now()) ? entry.data : null)
      }

      request.onerror = function() { resolve(null) }
    })
  }).catch(function() { return null })
}

/**
 * Store data in cache for a given user. Without user, no-op.
 *
 * @param {string} key
 * @param {Array} data
 * @param {number|string} scope - id del usuario de la sesión
 * @returns {Promise<void>}
 */
export function setCache(key, data, scope) {
  var fullKey = scopedKey(scope, key)
  if (!fullKey) return Promise.resolve()
  return openDB().then(function(db) {
    return new Promise(function(resolve) {
      var tx = db.transaction(STORE_NAME, 'readwrite')
      var store = tx.objectStore(STORE_NAME)
      store.put({
        scope: scope,
        data: data,
        timestamp: Date.now(),
        count: data.length,
      }, fullKey)

      tx.oncomplete = function() { resolve() }
      tx.onerror = function() { resolve() }
    })
  }).catch(function() {})
}

/**
 * Clear all cached data.
 */
export function clearCache() {
  return openDB().then(function(db) {
    return new Promise(function(resolve) {
      var tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).clear()
      tx.oncomplete = function() { resolve() }
      tx.onerror = function() { resolve() }
    })
  }).catch(function() {})
}
